import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Map as MapIcon, Network, Search, UserRound, Users } from "lucide-react";

import { api } from "@/lib/api";
import { MastersList } from "@/components/masters";
import { Badge, ChipRow, Empty, ErrorView, IconButton, Loading, SearchInput } from "@/components/kit";
import { C, date } from "@/lib/format";

function AgentItem({ item }) {
  return (
    <Link to={`/admin/agent/${item.id}`} data-testid={`admin-agent-item-${item.id}`} className="pressable flex flex-col gap-1 rounded-[20px] border border-[#333333] bg-[#1E1E1E] p-4">
      <div className="flex justify-between gap-2">
        <h3 className="t-h3 flex-1 truncate">{item.name}</h3>
        <span className="t-caption">{date(item.created_at)}</span>
      </div>
      <p className="t-caption truncate">{item.agency} · {item.city} ({item.department})</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <Badge label={item.has_rib ? "RIB renseigné" : "RIB manquant"} color={item.has_rib ? C.success : C.warning} />
        {!item.has_tiers && <Badge label="Barème à définir" color={C.warning} />}
        {!item.email_verified && <Badge label="E-mail non confirmé" color={C.silver} />}
        {item.referral_suspicious && <Badge label="Parrainage à vérifier" color={C.warning} />}
        <span className="t-caption">{item.recommendations} reco. · {item.signed} signé(s)</span>
      </div>
    </Link>
  );
}

function AgentsList() {
  const [search, setSearch] = useState("");
  const [f, setF] = useState("all");
  const q = useQuery({
    queryKey: ["admin-agents", search.trim(), f],
    queryFn: () => api(`/admin/agents?q=${encodeURIComponent(search.trim())}${f !== "all" ? `&rib=${f}` : ""}`),
    placeholderData: (prev) => prev,
  });
  return (
    <div className="flex flex-col">
      <div className="border-b border-[#222222] px-4 md:px-8">
        <div className="max-w-xl">
          <SearchInput icon={Search} testID="admin-agents-search-input" value={search} onChange={setSearch} placeholder="Nom, e-mail, agence, ville, code…" />
        </div>
        <ChipRow options={[{ key: "all", label: "Tous" }, { key: "no_tiers", label: "Sans barème" }, { key: "missing", label: "Sans RIB" }]} value={f} onChange={setF} testIDPrefix="admin-agents-filter" />
      </div>
      {q.isLoading ? <Loading /> : q.isError ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 p-4 pb-8 md:p-8">
          <p className="t-caption" data-testid="admin-agents-count">{q.data?.length ?? 0} agent(s)</p>
          {q.data?.length ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {q.data.map((a) => <AgentItem key={a.id} item={a} />)}
            </div>
          ) : (
            <Empty icon={Users} title="Aucun agent" text="Les agents inscrits apparaîtront ici." />
          )}
        </div>
      )}
    </div>
  );
}

const SEGS = [["agents", "Agents"], ["masters", "Maîtres d'œuvre"]];

export default function AdminNetwork() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [seg, setSeg] = useState(params.get("seg") === "masters" ? "masters" : "agents");
  const dept = params.get("dept") || undefined;
  useEffect(() => {
    const s = params.get("seg");
    if (s === "masters" || s === "agents") setSeg(s);
  }, [params]);
  const choose = (k) => {
    setSeg(k);
    setParams(k === "masters" ? { seg: k } : {}, { replace: true });
  };
  return (
    <div data-testid="admin-agents-screen" className="flex flex-col">
      <div className="px-4 pb-3 pt-4 md:px-8 md:pt-6">
        <div className="flex items-center gap-2">
          <h1 className="t-h1 flex-1">Réseau</h1>
          <IconButton icon={MapIcon} label="Carte & répartition" testID="admin-network-map-button" onClick={() => navigate("/admin/stats")} />
          <IconButton icon={UserRound} label="Clients" testID="admin-network-clients-button" onClick={() => navigate("/admin/clients")} />
          <IconButton icon={Network} label="Parrainages" testID="admin-agents-referrals-button" onClick={() => navigate("/admin/referrals")} />
        </div>
        <div role="tablist" className="mt-3 flex max-w-xl rounded-xl border border-[#333333] bg-[#1E1E1E] p-1">
          {SEGS.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={seg === k} data-testid={`admin-network-seg-${k}`} onClick={() => choose(k)}
              className={`focus-ring h-10 flex-1 rounded-lg text-sm font-medium transition-colors ${seg === k ? "bg-[#292415] text-[#D4AF37]" : "text-[#CCCCCC] hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {seg === "agents" ? <AgentsList /> : <MastersList initialDept={dept} />}
    </div>
  );
}
